// frontend/src/UserContext.js – Session Auth (cookie-based, refresh-safe)
// Build: 2026-05-05-writer-upgrade-gate-v1
//
// Fix:
// - register() supports Reader or direct Writer signup; Writer requires agreement acceptance.
// - Writer status is unlocked later through the server-controlled StoryHub upgrade flow.

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const UserContext = createContext(null);

function safeString(v) {
  return typeof v === "string" ? v : "";
}

function normalizeRole(role) {
  const r = String(role || "").toLowerCase().trim();
  if (r === "admin") return "admin";
  if (r === "writer" || r === "power") return "writer";
  if (r === "moderator") return "moderator";
  if (r === "user") return "user";
  return "user";
}

function extractUser(payload) {
  if (!payload) return null;

  if (payload.user && (payload.user.id || payload.user.email || payload.user.username)) {
    return payload.user;
  }

  if (payload.id || payload.email || payload.username) return payload;

  return null;
}

function setAuthFlag(isAuthed) {
  try {
    window.localStorage.setItem("ns.auth", isAuthed ? "1" : "0");
  } catch {}
}

async function clearDeviceBadge() {
  try {
    if ("clearAppBadge" in navigator) {
      await navigator.clearAppBadge();
    } else if ("setAppBadge" in navigator) {
      await navigator.setAppBadge(0);
    }
  } catch {}
}

async function synchronizeDeviceBadge() {
  try {
    if (!("setAppBadge" in navigator)) return;

    const data = await api.get("/api/push/badge-count");
    const count = Math.max(0, Number(data?.badgeCount || 0));

    if (count > 0) {
      await navigator.setAppBadge(count);
    } else {
      await clearDeviceBadge();
    }
  } catch {
    await clearDeviceBadge();
  }
}

function rethrowWithContext(err) {
  try {
    const e = err instanceof Error ? err : new Error(String(err?.message || err || "Request failed."));
    if (err?.response && !e.response) e.response = err.response;
    if (err?.data && !e.data) e.data = err.data;
    if (err?.status && !e.status) e.status = err.status;
    return e;
  } catch {
    return err;
  }
}

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);

  const [username, setUsername] = useState(() => {
    try {
      return safeString(window.localStorage.getItem("ns_username")).trim();
    } catch {
      return "";
    }
  });

  useEffect(() => {
    try {
      if (username) window.localStorage.setItem("ns_username", username);
      else window.localStorage.removeItem("ns_username");
    } catch {}
  }, [username]);

  useEffect(() => {
    if (!user) return undefined;

    function syncWhenVisible() {
      if (document.visibilityState === "visible") {
        void synchronizeDeviceBadge();
      }
    }

    function handleServiceWorkerMessage(event) {
      if (event?.data?.type === "NEXTSCENES_PUSH_RECEIVED") {
        void synchronizeDeviceBadge();
      }
    }

    void synchronizeDeviceBadge();

    window.addEventListener("focus", syncWhenVisible);
    document.addEventListener("visibilitychange", syncWhenVisible);

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener(
        "message",
        handleServiceWorkerMessage
      );
    }

    return () => {
      window.removeEventListener("focus", syncWhenVisible);
      document.removeEventListener("visibilitychange", syncWhenVisible);

      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener(
          "message",
          handleServiceWorkerMessage
        );
      }
    };
  }, [user]);

  async function applyGuest() {
    setUser(null);
    setUsername("");
    setAuthFlag(false);
    // Device badge cleanup must not delay navigating to Welcome back.
    void clearDeviceBadge();
  }

  async function applyUser(payload) {
    const me = extractUser(payload);
    if (!me) {
      await applyGuest();
      return null;
    }

    const normalized = { ...me, role: normalizeRole(me.role) };
    setUser(normalized);
    setUsername(normalized.name || normalized.username || normalized.email || "");
    setAuthFlag(true);
    // Badge synchronisation is independent of an authenticated page handoff.
    void synchronizeDeviceBadge();
    return normalized;
  }

  useEffect(() => {
    let alive = true;

    async function boot() {
      try {
        const mePayload = await api("/api/auth/me", { method: "GET" });
        if (!alive) return;
        await applyUser(mePayload);
      } catch {
        if (!alive) return;
        await applyGuest();
      } finally {
        if (!alive) return;
        setBooting(false);
      }
    }

    boot();

    return () => {
      alive = false;
    };
  }, []);

  // ✅ FIX: do not strip fields. Pass through roleChoice + writerAgreementAccepted.
  async function register(payload) {
    try {
      const safe = payload || {};
      return await api.post("/api/auth/register", {
        inviteCode: safe.inviteCode,
        username: safe.username,
        email: safe.email,
        password: safe.password,
        name: safe.name,

        // direct Writer signup is allowed when the agreement is accepted
        roleChoice: safe.roleChoice,
        writerAgreementAccepted: safe.writerAgreementAccepted,

        // reserved (future / group invite flow)
        inviteToken: safe.inviteToken,
      });
    } catch (err) {
      throw rethrowWithContext(err);
    }
  }

  async function resendVerification({ email }) {
    try {
      return await api.post("/api/auth/resend-verification", { email });
    } catch (err) {
      throw rethrowWithContext(err);
    }
  }

  async function login({ login, password }) {
    try {
      const data = await api.post("/api/auth/login", { login, password });
      await applyUser(data);
      return data;
    } catch (err) {
      await applyGuest();
      throw rethrowWithContext(err);
    }
  }


  async function upgradeToWriter({ accepted } = {}) {
    try {
      const data = await api.upgradeToWriter({ accepted: accepted === true });
      await applyUser(data);
      return data;
    } catch (err) {
      throw rethrowWithContext(err);
    }
  }

  async function logout() {
    try {
      await api.post("/api/auth/logout", {});
    } catch {
      // ignore
    } finally {
      await applyGuest();
    }
  }

  const value = useMemo(() => {
    const isAuthenticated = Boolean(user);
    const role2 = isAuthenticated ? normalizeRole(user?.role) : "guest";

    const isMember = isAuthenticated;
    const isPlainUser = role2 === "user";

    return {
      booting,
      isAuthenticated,
      user,
      username,
      setUsername,
      register,
      login,
      logout,
      resendVerification,

      role: role2,

      isGuest: !isAuthenticated,

      isUser: isMember,
      isMember: isMember,

      isPlainUser: isPlainUser,
      isWriter: role2 === "writer" || role2 === "moderator",
      isModerator: role2 === "moderator",
      isAdmin: role2 === "admin",
      isContentSteward: role2 === "admin" || user?.contentSteward === true,
      writerAgreementAccepted: user?.writerAgreementAccepted === true,
      writerAgreementAcceptedAt: user?.writerAgreementAcceptedAt || null,
      writerAgreementVersion: user?.writerAgreementVersion || "",
      upgradeToWriter,

      refreshMe: async () => {
        try {
          const mePayload = await api("/api/auth/me", { method: "GET" });
          return await applyUser(mePayload);
        } catch {
          await applyGuest();
          return null;
        }
      },
    };
  }, [user, username, booting]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  return useContext(UserContext);
}

export { UserContext };
